import {
  PendingWithholdingStatus,
  WithholdingTransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  WSActions,
  WSAlert,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { getCurrentTaxPaymentQuarter } from "@wingspanhq/utils/dist/date/taxDeadlines";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccountBalance } from "../../../query/banking/queries/useQueryInternalAccountBalance";
import { PATH_TAXES_SCHEDULE_PAYMENT } from "../paths";
import { useQueryListInternalAccountTransactions } from "../../../query/banking/queries/useQueryListInternalAccountTransactions";
import { NextPaymentInfo } from "./NextPaymentInfo";
import { useGoToTransfer } from "../../Transfer/paths";
import { useModalCancelPendingWithholding } from "../modals/ModalCancelPendingWithholding";
import { QUARTER_TO_TAX_QUARTER } from "../utils";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { Currency } from "@wingspanhq/users";

type Props = {
  internalAccountId?: string;
};

export const AvailableBalance: React.FC<Props> = props => {
  const history = useHistory();
  const goToTransfer = useGoToTransfer();
  const modalCancelPendingWithholding = useModalCancelPendingWithholding();
  const queryInternalAccounts = useQueryInternalAccounts();
  const internalAccount = queryInternalAccounts.data?.find(
    selectorActiveTaxWithholdingAccount
  );

  const queryBalance = useQueryInternalAccountBalance(
    props.internalAccountId!,
    {
      enabled: !!props.internalAccountId
    }
  );

  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!internalAccount
    }
  );

  const queryProcessingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Processing"
    },
    {
      enabled: !!internalAccount
    }
  );

  const internalPendingTransactions = useMemo(() => {
    return (queryPendingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryPendingTransactions.data?.pages]);

  const internalProcessingTransactions = useMemo(() => {
    return (queryProcessingTransactions.data?.pages || []).flatMap(
      page => page.transactions
    );
  }, [queryProcessingTransactions.data?.pages]);

  const quarterInfo = useMemo(() => getCurrentTaxPaymentQuarter(), []);
  const currentTaxQuarter = QUARTER_TO_TAX_QUARTER[quarterInfo.quarter];

  const scheduledPayments = useMemo(() => {
    if (!internalPendingTransactions) return [];
    return internalPendingTransactions.filter(
      transaction =>
        transaction.status === PendingWithholdingStatus.Pending &&
        (transaction.type as WithholdingTransactionType) ===
          WithholdingTransactionType.TaxPayment
    );
  }, [internalPendingTransactions, quarterInfo.year, currentTaxQuarter]);

  const processingPayments = useMemo(() => {
    if (!internalProcessingTransactions || !internalPendingTransactions)
      return [];
    const processingPayments = internalProcessingTransactions.filter(
      transaction => transaction.status === "Processing"
    );
    const pendingPayments = internalPendingTransactions.filter(
      transaction =>
        transaction.status === PendingWithholdingStatus.Pending &&
        (transaction.type as WithholdingTransactionType) !==
          WithholdingTransactionType.TaxPayment
    );
    return [...processingPayments, ...pendingPayments];
  }, [
    internalProcessingTransactions,
    internalPendingTransactions,
    quarterInfo.year,
    currentTaxQuarter
  ]);

  const scheduledPaymentAmount = useMemo(
    () => scheduledPayments.reduce((sum, t) => sum + t.amount, 0),
    [scheduledPayments]
  );

  const processingPaymentAmount = useMemo(
    () => processingPayments.reduce((sum, t) => sum + t.amount, 0),
    [processingPayments]
  );

  const hasScheduledPayment = scheduledPayments.length > 0;
  const hasProcessingPayment = processingPayments.length > 0;
  const hasScheduledTaxPayment = scheduledPayments.some(
    t =>
      (t.type as WithholdingTransactionType) ===
      WithholdingTransactionType.TaxPayment
  );
  const hasProcessingTaxPayment = processingPayments.some(
    t =>
      (t.type as WithholdingTransactionType) ===
      WithholdingTransactionType.TaxPayment
  );

  const availableBalance = queryBalance.data?.available || 0;
  const currentBalance = queryBalance.data?.balance || 0;
  const currency = queryBalance.data?.currency || "USD";

  if (!props.internalAccountId) {
    return (
      <WSCard header="Available balance">
        <WSList gap="XL">
          <WSText.Heading1 weight="book" color="gray300">
            $--
          </WSText.Heading1>
          <NextPaymentInfo />
        </WSList>
      </WSCard>
    );
  }

  return (
    <WSCard header="Available balance" shimmer={queryBalance.isInitialLoading}>
      <WSList gap="XL">
        <WSText.Heading1 weight="book" color="gray500">
          {formatMoney(availableBalance, currency)}
        </WSText.Heading1>

        {currentBalance ? (
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm color="gray500">
              Total balance
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray600">
              {formatMoney(currentBalance, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        ) : null}

        <WSDivider />

        {hasScheduledPayment ? (
          <>
            <WSText.Paragraph weight="medium">Upcoming</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Scheduled payment
                </WSText.ParagraphSm>
                <WSIcon name="info-circle-fill" size="XS" color="gray500" />
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(scheduledPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
          </>
        ) : null}

        {hasProcessingPayment ? (
          <>
            <WSText.Paragraph weight="medium">Processing now</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Pending amounts
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(processingPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
            {hasProcessingTaxPayment ? (
              <WSAlert
                theme="neutral"
                title="Payment processing"
                icon="info-circle"
              >
                No changes or cancellations to scheduled payments can be made
                for this quarter to ensure on-time processing and avoid late
                penalties.
              </WSAlert>
            ) : null}
          </>
        ) : null}

        <WSActions
          alignment="fill"
          buttons={[
            ...(hasProcessingTaxPayment
              ? [
                  {
                    label: "Edit scheduled payment",
                    kind: "Secondary" as const,
                    disabled: true
                  }
                ]
              : hasScheduledTaxPayment
              ? [
                  {
                    label: "Cancel scheduled payment",
                    kind: "Secondary" as const,
                    destructive: true,
                    onClick: () => {
                      const payment = scheduledPayments[0];
                      modalCancelPendingWithholding.open({
                        pendingWithholdingId: payment.transactionId,
                        amount: payment.amount,
                        currency: payment.currency as Currency,
                        date: payment.createdAt
                      });
                    }
                  }
                ]
              : [
                  {
                    label: "Schedule tax payment",
                    kind: "Secondary" as const,
                    onClick: () => {
                      history.push(PATH_TAXES_SCHEDULE_PAYMENT);
                    }
                  }
                ]),
            {
              label: "Transfer",
              kind: "Primary",
              onClick: () => {
                goToTransfer({
                  balance: "taxWithholdings"
                });
              }
            }
          ]}
        />

        <NextPaymentInfo />
      </WSList>
    </WSCard>
  );
};
